import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { ENV } from '../../../config/env';
import toast from 'react-hot-toast';

interface BusinessProfileData {
  headline: string;
  summary: string;
  aboutSection: string;
  serviceDescriptions: string[];
  keyFeatures: string[];
  callToAction: string;
  recommendedHashtags: string[];
}

const BusinessProfileTab: React.FC = () => {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [industry, setIndustry] = useState('');
  const [services, setServices] = useState<string[]>(['']);
  const [targetAudience, setTargetAudience] = useState('');
  const [uniqueSellingPoints, setUniqueSellingPoints] = useState<string[]>(['']);
  const [businessValues, setBusinessValues] = useState('');
  const [profileData, setProfileData] = useState<BusinessProfileData | null>(null);

  const handleAddService = () => {
    setServices([...services, '']);
  };

  const handleServiceChange = (index: number, value: string) => {
    const updatedServices = [...services];
    updatedServices[index] = value;
    setServices(updatedServices);
  };

  const handleRemoveService = (index: number) => {
    if (services.length > 1) {
      const updatedServices = [...services];
      updatedServices.splice(index, 1);
      setServices(updatedServices);
    }
  };

  const handleAddSellingPoint = () => {
    setUniqueSellingPoints([...uniqueSellingPoints, '']);
  };

  const handleSellingPointChange = (index: number, value: string) => {
    const updatedPoints = [...uniqueSellingPoints];
    updatedPoints[index] = value;
    setUniqueSellingPoints(updatedPoints);
  };

  const handleRemoveSellingPoint = (index: number) => {
    if (uniqueSellingPoints.length > 1) {
      const updatedPoints = [...uniqueSellingPoints];
      updatedPoints.splice(index, 1);
      setUniqueSellingPoints(updatedPoints);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const filteredServices = services.filter(service => service.trim() !== '');
    const filteredSellingPoints = uniqueSellingPoints.filter(point => point.trim() !== '');
    
    if (!businessName || !industry || filteredServices.length === 0 || !targetAudience || filteredSellingPoints.length === 0 || !businessValues) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const userId = await getToken();
      const response = await fetch(
        `${ENV.API_URL}/api/marketing/linkedin-business-profile`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            userId,
            businessName, 
            industry, 
            services: filteredServices, 
            targetAudience, 
            uniqueSellingPoints: filteredSellingPoints, 
            businessValues 
          }),
        }
      );
      
      const data = await response.json();
      setProfileData(data.profileData);
      toast.success('LinkedIn business profile optimization complete!');
    } catch (error) {
      console.error('Error optimizing LinkedIn business profile:', error);
      toast.error('Failed to optimize LinkedIn business profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const exampleData = {
    businessName: 'Acme Solutions',
    industry: 'Software Development',
    services: ['Custom SaaS Platforms', 'Cloud Migration', 'API Integrations'],
    targetAudience: 'Mid-sized businesses looking to modernize their operations',
    uniqueSellingPoints: ['Fast delivery', 'Enterprise security', 'Dedicated support'],
    businessValues: 'We believe in innovation, transparency, and helping clients scale efficiently.'
  };

  const handleUseExample = () => {
    setBusinessName(exampleData.businessName);
    setIndustry(exampleData.industry);
    setServices(exampleData.services);
    setTargetAudience(exampleData.targetAudience);
    setUniqueSellingPoints(exampleData.uniqueSellingPoints);
    setBusinessValues(exampleData.businessValues);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Input Form */}
      <div className="rounded-xl bg-white/60 backdrop-blur-sm border border-white/40 shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">
            Business Profile Details
          </h2>
          <button
            type="button"
            className="text-xs px-3 py-1.5 bg-white/60 backdrop-blur-sm rounded-lg border border-white/40 hover:bg-white transition-all duration-200 font-medium text-cyan-700"
            onClick={handleUseExample}
          >
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Try Example
            </span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="businessName" className="block text-sm font-semibold text-gray-700 mb-2">
              Business Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="businessName"
              className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="e.g. Acme Solutions"
              required
            />
          </div>
          
          <div>
            <label htmlFor="industry" className="block text-sm font-semibold text-gray-700 mb-2">
              Industry <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="industry"
              className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="e.g. Software Development, Marketing Agency"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Services Offered
            </label>
            {services.map((service, index) => (
              <div key={index} className="flex mb-2 last:mb-0">
                <input
                  type="text"
                  className="flex-grow px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  value={service}
                  onChange={(e) => handleServiceChange(index, e.target.value)}
                  placeholder={`Service ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveService(index)}
                  className="bg-red-500 text-white px-3 rounded-r-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={services.length <= 1}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddService}
              className="mt-2 inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm rounded-lg border border-gray-200 hover:bg-white transition-all text-sm font-medium text-cyan-700"
            >
              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Service
            </button>
          </div>
          
          <div>
            <label htmlFor="targetAudience" className="block text-sm font-semibold text-gray-700 mb-2">
              Target Audience
            </label>
            <textarea
              id="targetAudience"
              className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              placeholder="Describe your ideal clients or customers"
              rows={3}
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Unique Selling Points
            </label>
            {uniqueSellingPoints.map((point, index) => (
              <div key={index} className="flex mb-2 last:mb-0">
                <input
                  type="text"
                  className="flex-grow px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  value={point}
                  onChange={(e) => handleSellingPointChange(index, e.target.value)}
                  placeholder={`Selling Point ${index + 1}`}
                />
                <button
                  type="button"
                  onClick={() => handleRemoveSellingPoint(index)}
                  className="bg-red-500 text-white px-3 rounded-r-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={uniqueSellingPoints.length <= 1}
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddSellingPoint}
              className="mt-2 inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm rounded-lg border border-gray-200 hover:bg-white transition-all text-sm font-medium text-cyan-700"
            >
              <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Selling Point
            </button>
          </div>
          
          <div>
            <label htmlFor="businessValues" className="block text-sm font-semibold text-gray-700 mb-2">
              Business Values & Mission
            </label>
            <textarea
              id="businessValues"
              className="w-full px-4 py-2.5 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
              value={businessValues}
              onChange={(e) => setBusinessValues(e.target.value)}
              placeholder="Describe your company's values, mission, and vision"
              rows={4}
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 px-6 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200 ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:-translate-y-0.5'}`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Optimizing...
              </span>
            ) : 'Optimize LinkedIn Business Profile'}
          </button>
        </form>
      </div>

      {/* Results Panel */}
      <div>
        {profileData ? (
          <div className="rounded-xl bg-white/60 backdrop-blur-sm border border-white/40 shadow-sm p-6">
            <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600 mb-6">
              Optimized Business Profile
            </h2>
            
            <div className="space-y-5">
              <div className="bg-white/70 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-cyan-700 mb-2">Headline</h3>
                <p className="text-gray-700 font-medium">{profileData.headline}</p>
              </div>
              
              <div className="bg-white/70 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-cyan-700 mb-2">Summary</h3>
                <p className="text-gray-700 text-sm whitespace-pre-line">{profileData.summary}</p>
              </div>
              
              <div className="bg-white/70 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-cyan-700 mb-2">About Section</h3>
                <p className="text-gray-700 text-sm whitespace-pre-line">{profileData.aboutSection}</p>
              </div>
              
              <div className="bg-white/70 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-cyan-700 mb-3">Service Descriptions</h3>
                <div className="space-y-2">
                  {profileData.serviceDescriptions.map((desc, index) => (
                    <div key={index} className="bg-white/80 p-3 rounded-lg border border-gray-100">
                      <p className="text-gray-700 text-sm whitespace-pre-line">{desc}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white/70 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-cyan-700 mb-3">Key Features & Benefits</h3>
                <ul className="list-disc pl-5 space-y-1">
                  {profileData.keyFeatures.map((feature, index) => (
                    <li key={index} className="text-gray-700 text-sm">{feature}</li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-white/70 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-cyan-700 mb-2">Call to Action</h3>
                <p className="text-gray-700 text-sm">{profileData.callToAction}</p>
              </div>
              
              <div className="bg-white/70 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-cyan-700 mb-3">Recommended Hashtags</h3>
                <div className="flex flex-wrap gap-2">
                  {profileData.recommendedHashtags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-cyan-500 to-blue-500 text-white"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-xl bg-white/40 backdrop-blur-sm border border-gray-200 border-dashed p-12 text-center h-full flex flex-col items-center justify-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Profile Optimization Yet</h3>
            <p className="text-gray-600 text-sm">
              Fill out the form and click "Optimize LinkedIn Business Profile" to see your results here.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessProfileTab;
